/**
 * <xenon-set-offer> — the "bu ürün bir setin parçası" offer on the PDP.
 *
 * ONE line, ONE variant, ONE button, which is the whole design and the reason
 * this is a plain fetch rather than a form. The PDP already carries
 * product-form-{sectionId}, and complementary-look.js registers a DOCUMENT-LEVEL
 * CAPTURE submit listener on it — precisely because listeners on the target fire
 * in registration order regardless of phase. A submit button here would walk
 * straight into that interception and be batched into an add for a product the
 * form is not about. So: type="button", outside any form, no submit event.
 *
 * AUTONOMOUS, not a customized built-in. `is=` works in this theme (vendor.js
 * ships the @ungap polyfill, which is=product-form relies on), but an autonomous
 * element has no polyfill dependency and gives the status live region a natural
 * home on the element itself.
 */
if (!customElements.get('xenon-set-offer')) {
  /* How long "Sepete Eklendi" stays before the button returns to rest. Mostly
     seen AFTER the drawer is dismissed — which is exactly the keyboard user who
     needs the confirmation. */
  const ADDED_FEEDBACK_MS = 1600;

  customElements.define(
    'xenon-set-offer',
    class XenonSetOffer extends HTMLElement {
      connectedCallback() {
        if (this.dataset.bound === 'true') return;

        this.onClick = this.onClick.bind(this);
        this.bindLayout();
        this.bindPicker();

        this.dataset.bound = 'true';
      }

      /**
       * Binds whatever layout is live RIGHT NOW. Called on connect and again
       * after every swap, because the swap replaces the layout element outright
       * — button, status region and scroller are new nodes and the old
       * listeners went with the old ones.
       */
      bindLayout() {
        this.button = this.querySelector('[data-xset-add]');
        this.status = this.querySelector('[data-xset-status]');
        this.scroller = this.querySelector('.xenon-set-offer__scroller');

        /* A half-bound element is worse than an unbound one, and both the theme
           editor and the Section Rendering API re-run this. With the `link`
           behaviour there is no button at all and nothing here applies. */
        if (!this.button || !this.dataset.variantId) {
          this.syncScroller();
          this.observeScroller();
          return;
        }

        this.idleLabel = this.button.querySelector('.btn__text')?.textContent.trim() || 'Seti Sepete Ekle';
        this.submitting = false;

        this.button.addEventListener('click', this.onClick);

        this.syncScroller();
        this.observeScroller();
      }

      /* ------------------------------------------------------------------ */

      /**
       * The picker. Found by id rather than by descent because BasicModal moves
       * itself to <body> on upgrade (theme.js:731-736) — by the time a shopper
       * can click anything it is no longer inside this element.
       *
       * Opening is NOT handled here: the trigger carries aria-controls and
       * ModalComponent binds it. All this owns is the choice.
       */
      bindPicker() {
        const id = this.dataset.pickerId;
        if (!id) return;

        const picker = document.getElementById(id);
        if (!picker) return;

        this.picker = picker;
        this.markCurrent(this.dataset.setHandle);

        picker.addEventListener('click', (event) => {
          const row = event.target.closest('[data-xset-pick]');
          if (!row || !picker.contains(row)) return;
          event.preventDefault();
          this.pick(row.dataset.xsetPick);
        });
      }

      /**
       * Puts a different set in the panel.
       *
       * The alternatives were rendered by Liquid and parked in <template>s, so
       * this is a clone and a replace — no request, no spinner, no chance of the
       * prices in the panel disagreeing with the prices in the picker, because
       * both came out of the same loop on the same page load.
       */
      pick(handle) {
        const closePicker = () => {
          if (typeof this.picker?.hide === 'function') this.picker.hide();
        };

        if (!handle || handle === this.dataset.setHandle) {
          closePicker();
          return;
        }

        const template = this.querySelector(`template[data-xset-alt="${CSS.escape(handle)}"]`);
        const live = this.querySelector('[data-xset-layout]');
        const next = template?.content.firstElementChild?.cloneNode(true);
        if (!next || !live) {
          closePicker();
          return;
        }

        /* The old listener dies with the old node; nothing to remove. */
        live.replaceWith(next);

        /* The host's dataset is what the add-to-cart and its event read. */
        this.dataset.variantId = next.dataset.variantId || '';
        this.dataset.setHandle = next.dataset.setHandle || '';
        this.dataset.setValue = next.dataset.setValue || '';

        const badge = this.querySelector('[data-xset-badge]');
        if (badge) badge.textContent = next.dataset.setLabel || '';

        /* The panel's heading never names the set on screen — it is the four
           cards that do — so this hidden span is the only place a screen reader
           is told WHICH set the button below now adds. Letting it go stale would
           leave the panel silently offering something else. */
        const name = this.querySelector('[data-xset-setname]');
        if (name) name.textContent = `: ${next.dataset.setTitle || ''}`;

        this.bindLayout();
        this.markCurrent(handle);
        closePicker();

        /* NOT the panel's status line. That one is visible, and a sentence
           naming the set is noise beside a panel that has just redrawn itself
           to show exactly that — the shopper can see what they picked.
           announce() writes to a region that is only ever heard, for the
           shopper who cannot. */
        this.announce(`${next.dataset.setTitle || 'Set'} seçildi.`);
      }

      /**
       * The swap's only report, and it is screen-reader-only.
       *
       * A SEPARATE REGION, not the ledger's status line: that one lives inside
       * the layout and the swap replaces it, and a live region inserted in the
       * same tick as its text is not reliably announced. This one sits on the
       * host, outside everything the swap touches, so it is in the accessibility
       * tree long before it is written to.
       */
      announce(message) {
        const region = this.querySelector('[data-xset-live]');
        if (!region) return;
        region.textContent = message;
        if (this.announceTimer) clearTimeout(this.announceTimer);
        /* Cleared so the same pick announced twice in a row is heard twice. */
        this.announceTimer = setTimeout(() => {
          region.textContent = '';
        }, ADDED_FEEDBACK_MS);
      }

      /** aria-current is the row's state; the tick beside it is drawn off it. */
      markCurrent(handle) {
        if (!this.picker) return;
        this.picker.dataset.current = handle || '';
        this.picker.querySelectorAll('[data-xset-pick]').forEach((row) => {
          if (row.dataset.xsetPick === handle) {
            row.setAttribute('aria-current', 'true');
          } else {
            row.removeAttribute('aria-current');
          }
        });
      }

      /* ------------------------------------------------------------------ */

      disconnectedCallback() {
        /* A pending success timeout writing to a detached node is the documented
           reason card-product-xenon.js carries one of these. */
        if (this.timer) clearTimeout(this.timer);
        if (this.announceTimer) clearTimeout(this.announceTimer);
        this.resizeObserver?.disconnect();
      }

      /* ------------------------------------------------------------------ */

      /* The scroll region is given role="group" and tabindex="0" in the Liquid so
         it is keyboard-reachable before this script runs. When the whole equation
         fits there is nothing to scroll, and a focusable element with no scrolling
         to do is a dead tab stop — so the tabindex comes off. */
      syncScroller() {
        if (!this.scroller) return;
        const overflows = this.scroller.scrollWidth - this.scroller.clientWidth > 1;
        if (overflows) {
          this.scroller.setAttribute('tabindex', '0');
        } else {
          this.scroller.removeAttribute('tabindex');
        }
      }

      observeScroller() {
        /* Dropped before a new one is made: bindLayout runs again after every
           swap, and an observer still watching the scroller of a layout that is
           no longer in the document keeps a detached subtree alive and fires
           syncScroller against it. */
        this.resizeObserver?.disconnect();

        if (!this.scroller || typeof ResizeObserver === 'undefined') return;
        /* rAF-coalesced: this store measures INP, and a ResizeObserver that
           measures layout synchronously on every frame of a window drag is a way
           to lose that. */
        this.resizeObserver = new ResizeObserver(() => {
          if (this.raf) cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.syncScroller());
        });
        this.resizeObserver.observe(this.scroller);
      }

      /* ------------------------------------------------------------------ */

      /**
       * The VISIBLE line under the button. Only failures reach it now — the
       * progress and success copy went to announce(). Passing an empty message
       * restores the merchant's idle copy, if they set any.
       */
      setStatus(message, isError) {
        if (!this.status) return;
        this.status.textContent = message || this.status.dataset.xsetIdle || '';
        this.status.classList.toggle('xenon-set-offer__status--error', Boolean(isError));
      }

      setLabel(text) {
        const el = this.button.querySelector('.btn__text');
        if (el) el.textContent = text;
      }

      setBusy(busy) {
        /* NEVER `disabled`. card-product-xenon.js records why: a disabled button
           goes low-contrast AND silently swallows the second click, so the
           shopper gets no feedback at all from the press that was eaten.
           aria-disabled + the early return in onClick does the same job, and
           theme.css:2199 already dims [aria-disabled=true] for us.

           .btn--loading is opacity-only (theme.css:2402) inside a button that is
           width:100% and a fixed 48px tall, so nothing moves. */
        this.button.classList.toggle('btn--loading', busy);
        this.button.setAttribute('aria-disabled', String(busy));
      }

      /* ------------------------------------------------------------------ */

      onClick(event) {
        event.preventDefault();

        /* Both guards, deliberately. The flag covers the in-flight request; the
           attribute covers a click that arrives between the aria update and the
           flag being read by assistive tech driving the button. */
        if (this.submitting || this.button.getAttribute('aria-disabled') === 'true') return;

        const variantId = Number(this.dataset.variantId);
        if (!variantId) return;

        this.submitting = true;
        this.setBusy(true);
        /* HEARD, NOT SHOWN. The button is already spinning and already
           aria-disabled; a line of copy under it repeating that is noise for
           anyone who can see it. announce() puts it where only a screen reader
           picks it up, which is the half of this that was never redundant. */
        this.announce('Sepete ekleniyor…');

        this.addToCart(variantId);
      }

      addToCart(variantId) {
        /* MANDATORY, and first. assets/cart.js:212 reads
           event.cart.sections[sectionId] with NO guard: publishing cartUpdate
           without `sections` throws a TypeError, the drawer's subscriber dies
           mid-update, and the drawer then stays stale for the rest of the
           session. card-product-xenon.js:70-74 records catching this. */
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        /* Deleted so the browser writes the multipart boundary itself. */
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);
        config.body = formData;

        /* DELIBERATELY NO LINE-ITEM PROPERTY. complementary-look.js:620-627
           attaches `_birlikte_al` for attribution and records the cost: a line
           carrying a property is its OWN cart line, so the same variant added
           from two places shows twice. For a suggestion row that is acceptable.
           Here this section is a primary path to a ~20.000 TL SKU, and a shopper
           who adds the set here and again from the set's own page would get two
           of them. Attribution comes off the event below instead — same
           analytics, clean cart. */

        fetch(FoxTheme.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) {
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
                source: 'xenon-set-offer',
                productVariantId: variantId,
                errors: parsed.errors || parsed.description,
                message: parsed.message,
              });
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', { detail: { errorMessage: parsed.description } })
              );
              this.setStatus(parsed.description || parsed.message || 'Sepete eklenemedi.', true);
              return;
            }

            return fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })
              .then((response) => response.json())
              .then((cartJson) => {
                cartJson.sections = parsed.sections;

                /* Publish BEFORE show(), so the drawer's DOM is already
                   refreshed when it animates in; the other way round gives a
                   one-frame stale drawer. */
                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
                document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsed } }));

                document.dispatchEvent(
                  new CustomEvent('xenon:set-offer-added', {
                    detail: {
                      setHandle: this.dataset.setHandle,
                      fromHandle: this.dataset.fromHandle,
                      value: Number(this.dataset.setValue) || 0,
                    },
                  })
                );

                /* The button says it, the cart drawer opens on top of it, and
                   the status line stays empty: three confirmations of one add
                   was two too many. */
                this.setLabel('Sepete Eklendi');
                this.announce('Set sepetine eklendi.');

                /* The clock starts when the request STOPS, not when the click
                   landed: timing it from the click means a slow cart write burns
                   the whole window behind the spinner and lands back on idle
                   having confirmed nothing. */
                if (this.timer) clearTimeout(this.timer);
                this.timer = setTimeout(() => this.setLabel(this.idleLabel), ADDED_FEEDBACK_MS);

                /* One argument, two contracts: cart-drawer stores it as
                   activeElement (theme.js:791-795) and handleAfterHide returns
                   focus to it on close (theme.js:811-813). Nothing else to do.

                   complementary-look.js:673 opens the drawer here and
                   card-product-xenon.js:118-123 deliberately does not — on a
                   listing grid an overlay per add costs the shopper their place
                   in the row. That reasoning does not reach this section: one
                   considered, decisive add on a page the shopper is reading. */
                document.querySelector('cart-drawer')?.show(this.button);
              });
          })
          .catch((error) => {
            console.error('[xenon-set-offer]', error);
            this.setStatus('Sepete eklenemedi. Bağlantınızı kontrol edip tekrar deneyin.', true);
          })
          .finally(() => {
            /* The button is never left locked by a failure — that is what
               `finally` is for, and the shape complementary-look.js:679-684 uses. */
            this.submitting = false;
            this.setBusy(false);
          });
      }
    }
  );
}

/**
 * <xenon-set-picker> — the popup, and nothing but the popup.
 *
 * It adds no behaviour of its own: BasicModal already brings the overlay
 * transitions, Escape, the focus trap, the body scroll lock and the move to
 * <body>. It exists as its own element only so the picker can be styled and
 * found without competing with the theme's other drawers. The choosing lives in
 * <xenon-set-offer>, which owns the panel the choice changes.
 *
 * whenDefined, not a bare define: the class it extends is created by theme.js,
 * and this file is a section asset. look-info-modal.js takes the same precaution
 * for the same reason — defer order holds on the first paint and not afterwards,
 * when the theme editor re-renders a section and its scripts arrive by fetch.
 */
customElements.whenDefined('basic-modal').then(() => {
  if (customElements.get('xenon-set-picker')) return;
  customElements.define('xenon-set-picker', class extends customElements.get('basic-modal') {});
});
